import Sentry from '@grantstreet/sentry-multiplex'

/**
 * All the tags that could possibly exist on a flag.
 * Individual flags may not define all of these tags, but all
 * of the tags that are defined for a flag should be used
 * when getting the flag.
 *
 * When setting a flag's value in the config service, tags can be
 * omitted to provide fallback values when a specific tag+value
 * does not have a specific override value.
 */
export type FlagTags = Partial<{
  /**
   * The environment the flag is being evaluated in. This also controls
   * which Config service to use to look up the flag's value.
   */
  env: 'dev' | 'test' | 'beta' | 'stage' | 'demo' | 'prod'
  /**
   * The client identifier.
   */
  client: string
  /**
   * The site identifier.
   */
  site: string
}>

/**
 * Everything that can be stored in a flag in the Config
 * service. This includes the tags and the flag's value.
 */
export type FlagAttrs = FlagTags & { value?: unknown }

/**
 * Defines the order tags must be in on a flag. The config service
 * treats 'env=test/client=sunshine' as different from
 * 'client=sunshine/env=test', so we must have a standard order of tags
 * to prevent unexpected behavior.
 */
const flagOrder: (keyof FlagTags)[] = ['env', 'client', 'site']

/**
 * A common, arbitrary flag. The flag's value could be anything.
 */
export class Flag {
  /**
   * The name of the flag, like 'ticket/PSC-1234'
   */
  name: string

  /**
   * Tags used to fetch the flag. Tags are used for inheritance.
   */
  tags: FlagTags = {}

  /**
   * The flag's value, used to determine if a feature should be used.
   */

  value?: unknown

  /**
   * All the flag's attributes from the Config service. If this is not
   * set, we failed to get the flag from the Config service.
   */
  attrs?: FlagAttrs

  /**
   * Construct a new Flag object with the given tags and value.
   */
  constructor (name: string, tags: FlagTags = {}, value: unknown = undefined) {
    this.name = name
    this.value = value
    this.tags = tags
  }

  /**
   * A map of flag name to Flag object of flags that have already been
   * requested.
   */
  // eslint-disable-next-line no-use-before-define
  private static attrCache: { [key: string]: FlagAttrs | null } = {}

  /**
   * Clear all cached flags.
   */
  static clearCache (): void {
    Flag.attrCache = {}
  }

  /**
   * Fetch a Flag from the Config service using the given name
   * and tags. This will never throw, but will instead return a flag
   * with the defaultValue.
   * Will only attempt to fetch the flag once. After that, the same
   * value will be returned for internal consistency.
   *
   * @field defaultValue - Value to use when an error occurs
   */
  static async fetch (name: string, tags: FlagTags = {}, defaultValue: unknown = undefined): Promise<Flag> {
    if (!(name in Flag.attrCache)) {
      const configHost = 'env' in tags && tags.env && tags.env !== 'prod' ? 'beta-config.grantstreet.com' : 'config.grantstreet.com'

      // When we use tags, we need to use them in the correct order. We
      // can't use later tags before earlier tags. Each flag can have its
      // own tags, so it's really up to the caller to make sure they specify
      // all the tags their flag uses. But the order is enforced so that
      // folks don't go crazy with what tags mean.
      let tagsPathPart = flagOrder.filter(k => k in tags).map(k => `${k}:${tags[k]}`).join('/')
      if (tagsPathPart && !tagsPathPart.endsWith('/')) {
        tagsPathPart += '/'
      }
      const flagPath = `/flags/data-pub/${tagsPathPart}${name}`
      const baseKey = `${flagPath}/`

      try {
        const res = await fetch(`https://${configHost}/api/v1${flagPath}`)
        const resAttrs = Object.fromEntries(
          Object.entries(await res.json()).map(([k, v]) => [k.slice(baseKey.length), v]),
        )
        if (!('value' in resAttrs)) {
          throw new Error('No "value" in flag attributes')
        }
        Flag.attrCache[name] = resAttrs
      }
      catch (error) {
        Sentry.captureException(
          new FlagFetchError(name),
          {
            originalException: error,
            captureContext: {
              level: 'info',
              tags: { flag: name, ...tags },
            },
          },
        )
        Flag.attrCache[name] = null
      }
    }

    const attrs = Flag.attrCache[name]
    if (attrs === null) {
      return new Flag(name, tags, defaultValue)
    }
    const flag = new Flag(name, tags, attrs.value)
    flag.attrs = attrs
    return flag
  }
}

export class FlagFetchError extends Error {
  constructor (name: string) {
    super(`Unable to fetch flag "${name}"`)
  }
}

export class FlagValueTypeError extends Error {
  constructor (name: string, expectedType: string) {
    super(`Flag "${name}" value not expected type ${expectedType}`)
  }
}

/**
 * BooleanFlag wraps a flag with a "value" field that is boolean: `true`
 * or `false`.
 */
export class BooleanFlag extends Flag {
  value: boolean = false
  constructor (name: string, tags: FlagTags = {}, value: boolean = false) {
    super(name, tags, value)
  }

  static async fetch (name: string, tags: FlagTags = {}, defaultValue: boolean = false): Promise<BooleanFlag> {
    const flag = await Flag.fetch(name, tags, defaultValue)
    if (typeof flag.value !== 'boolean') {
      Sentry.captureException(
        new FlagValueTypeError(name, 'boolean'),
        {
          tags: { flag: flag.name },
        },
      )
      flag.value = false
    }
    return Object.assign(new BooleanFlag(name), flag)
  }

  toString (): string {
    return this.value ? 'true' : 'false'
  }
}
